/**
 * STRUCT CODE v2 API Service
 * 正確な西洋占星術エンジンを使用した新しいAPIとの通信
 */

import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_V2_URL || 'https://struct-code-production.up.railway.app/api/v2';

class StructCodeV2Service {
  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      headers: {
        'Content-Type': 'application/json',
      },
      timeout: 120000, // 診断は時間がかかる可能性があるため2分に設定
    });
  }

  /**
   * ヘルスチェック
   */
  async healthCheck() {
    try {
      const response = await this.client.get('/health');
      return response.data;
    } catch (error) {
      console.error('Health check failed:', error);
      throw this._handleError(error);
    }
  }

  /**
   * 診断を実行
   * @param {Object} diagnosisData - 診断データ
   * @param {string} diagnosisData.birth_date - 生年月日 (YYYY-MM-DD)
   * @param {string} diagnosisData.birth_location - 出生地
   * @param {Array} diagnosisData.answers - 25問の回答
   * @param {string} [diagnosisData.birth_time] - 出生時刻 (HH:MM) 任意
   * @returns {Promise<Object>} 診断結果
   */
  async createDiagnosis(diagnosisData) {
    try {
      const response = await this.client.post('/diagnosis', diagnosisData);
      return response.data;
    } catch (error) {
      console.error('Diagnosis creation failed:', error);
      throw this._handleError(error);
    }
  }

  /**
   * 診断結果を取得
   * @param {string} diagnosisId - 診断ID
   * @returns {Promise<Object>} 診断結果
   */
  async getDiagnosis(diagnosisId) {
    try {
      const response = await this.client.get(`/diagnosis/${diagnosisId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get diagnosis:', error);
      throw this._handleError(error);
    }
  }

  /**
   * 全タイプ一覧を取得
   * @returns {Promise<Array>} 24タイプの配列
   */
  async getAllTypes(lang = 'ja') {
    try {
      const response = await this.client.get('/types', { params: { lang } });
      return response.data;
    } catch (error) {
      console.error('Failed to get all types:', error);
      throw this._handleError(error);
    }
  }

  /**
   * 特定タイプの詳細を取得
   * @param {string} typeCode - タイプコード (例: ACCP, JDPU)
   * @param {string} lang - 言語 ('ja' or 'en')
   * @returns {Promise<Object>} タイプ詳細
   */
  async getTypeDetails(typeCode, lang = 'ja') {
    try {
      const response = await this.client.get(`/types/${typeCode}`, { params: { lang } });
      return response.data;
    } catch (error) {
      console.error(`Failed to get type details for ${typeCode}:`, error);
      throw this._handleError(error);
    }
  }

  /**
   * エラーハンドリング
   * @private
   */
  _handleError(error) {
    if (error.response) {
      // サーバーからのエラーレスポンス
      const { status, data } = error.response;
      return {
        message: data.detail || data.message || 'サーバーエラーが発生しました',
        status,
        data,
      };
    } else if (error.request) {
      // リクエストは送信されたがレスポンスがない
      return {
        message: 'サーバーに接続できません。ネットワーク接続を確認してください。',
        status: 0,
      };
    } else {
      // その他のエラー
      return {
        message: error.message || '予期しないエラーが発生しました',
        status: -1,
      };
    }
  }

  /**
   * 軸の値をH/M/Lに分類
   * @param {number} value - 軸の値 (0.0 - 1.0)
   * @returns {string} H, M, または L
   */
  classifyAxisValue(value) {
    if (value >= 0.60) return 'H';
    if (value >= 0.40) return 'M';
    return 'L';
  }

  /**
   * STRUCT CODEを整形
   * @param {Object} diagnosis - 診断結果
   * @returns {string} 整形されたSTRUCT CODE
   */
  formatStructCode(diagnosis) {
    const { struct_type, axes } = diagnosis;
    const values = [
      Math.round(axes['起動軸'] * 1000),
      Math.round(axes['判断軸'] * 1000),
      Math.round(axes['選択軸'] * 1000),
      Math.round(axes['共鳴軸'] * 1000),
      Math.round(axes['自覚軸'] * 1000),
    ];
    return `${struct_type} ${values.join('-')}`;
  }
}

// シングルトンインスタンスをエクスポート
const structCodeV2Service = new StructCodeV2Service();
export default structCodeV2Service;
